// 의뢰서 작성 [명부에서 선택] 모달 — QR 등록 레코드 중 active·필수 동의 완료 건만(단골·동의 없음은 연결 대상 아님)
// W9(덴클 케어 1차, 2026-10-04 대표 결정 "앱 등록 즉시 성립"의 보완책 — 엉뚱한 레코드 선택으로 보철 기록이 남에게 가는 것 방지):
//   ①「앱 등록」 배지 ②같은 이름 2명 이상이면 「동명이인 주의」 + 상단 경고(정본 v2 L-5) ③생년월일 · ***-끝4자리(L-4, 없으면 "번호 없음").
//   앱 등록 환자를 고르면 CreateOrder 가 연결 확인 창(L-6)을 띄운다.
//   표시만 — 직원에게 추가 클릭을 요구하지 않는다. 동명이인 수는 연결 가능 여부와 무관하게 명부 전체(삭제 제외)에서 센다.
import React, { useEffect, useMemo, useState } from 'react';
import { Search, X } from 'lucide-react';
import { unifiedStyles as U } from '../../styles/Styles';
import { listClinicPatientRecords, describeError, isAppRegisteredRecord, appRegisterBadgeLabel, sameNameCountMap, sameNameCountOf } from '../../services/clinicPatientsApi';

function ClinicPatientPicker({ clinicId, initialTerm = '', onSelect, onClose }) {
    const [rows, setRows] = useState([]);
    const [nameCounts, setNameCounts] = useState(() => new Map());   // 이름 정규화 키 → 명부 인원(삭제 제외)
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [term, setTerm] = useState(initialTerm);

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const r = await listClinicPatientRecords(clinicId);
                // active + 필수 동의(collect·sensitive·delegation agreed) 있는 레코드만 연결 가능(검토 ③) — 동의 없음은 명부에 배지로 사유 표시
                // deleteRequested(환자 삭제 요청)·deleted 는 status !== 'active' 로 제외 — 삭제 요청 중인 환자에게 새 의뢰서를 연결하지 않는다(계약 §3-15)
                if (alive) { setRows(r.filter(x => x.status === 'active' && x.consentOk)); setNameCounts(sameNameCountMap(r)); }
            } catch (e) {
                if (alive) setError(describeError(e));
            } finally { if (alive) setLoading(false); }
        })();
        return () => { alive = false; };
    }, [clinicId]);

    const filtered = useMemo(() => {
        const t = term.trim().toLowerCase();
        if (!t) return rows;
        return rows.filter(r => (r.name || '').toLowerCase().includes(t) || (r.phoneLast4 || '').includes(t));
    }, [rows, term]);
    // 정본 v2 L-5 — 보이는 목록 중 가장 많은 동명이인 수(0=경고 없음)
    const dupMax = useMemo(() => filtered.reduce((m, r) => { const n = sameNameCountOf(nameCounts, r.name); return n >= 2 && n > m ? n : m; }, 0), [filtered, nameCounts]);

    return (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
            <div style={{ ...U.card, width: '100%', maxWidth: 520, maxHeight: '80vh', display: 'flex', flexDirection: 'column' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                    <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: '#0f172a' }}>명부에서 환자 선택</h3>
                    <button type="button" onClick={onClose} style={{ ...U.buttonSecondary, padding: '4px 8px' }}><X size={14} /></button>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, border: '1px solid #e2e8f0', borderRadius: 8, padding: '6px 10px', marginBottom: 10 }}>
                    <Search size={14} color="#94a3b8" />
                    <input autoFocus value={term} onChange={(e) => setTerm(e.target.value)} placeholder="이름 또는 뒤 4자리"
                        style={{ flex: 1, border: 'none', outline: 'none', fontSize: 13 }} />
                </div>
                {dupMax >= 2 && (
                    <div style={{ background: '#fff7ed', border: '1px solid #fed7aa', color: '#9a3412', borderRadius: 8, padding: '8px 12px', fontSize: 12.5, marginBottom: 8, lineHeight: 1.5 }}>
                        {`같은 이름의 환자가 ${dupMax}명 있습니다. 생년월일과 휴대폰 번호 끝 4자리로 구분해 주세요.`}
                    </div>
                )}
                {error && <div style={{ background: '#fee2e2', color: '#b91c1c', borderRadius: 8, padding: '8px 12px', fontSize: 13, marginBottom: 8 }}>{error}</div>}
                <div style={{ overflow: 'auto', flex: 1 }}>
                    {loading ? <div style={{ padding: 20, textAlign: 'center', color: '#64748b', fontSize: 13 }}>불러오는 중...</div>
                        : filtered.length === 0 ? <div style={{ padding: 20, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>{rows.length === 0 ? '연결 가능한 환자(QR 등록·동의 완료)가 없습니다.' : '검색 결과가 없습니다.'}</div>
                            : filtered.map(r => {
                                const dup = sameNameCountOf(nameCounts, r.name) >= 2;
                                const app = isAppRegisteredRecord(r);
                                return (
                                    <button key={r.id} type="button" onClick={() => onSelect(r)}
                                        style={{ display: 'flex', width: '100%', justifyContent: 'space-between', alignItems: 'center', gap: 8, padding: '10px 8px', border: 'none', borderBottom: '1px solid #f1f5f9', background: dup ? '#fffbf5' : 'transparent', cursor: 'pointer', textAlign: 'left' }}>
                                        <span style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 6, minWidth: 0 }}>
                                            <span style={{ fontSize: 14, fontWeight: 600, color: '#0f172a' }}>{r.name || '(이름 없음)'}{r.gender && <span style={{ color: '#94a3b8', fontWeight: 400, marginLeft: 6, fontSize: 12 }}>{r.gender === 'F' ? '여' : '남'}</span>}</span>
                                            {app && <span style={{ ...U.badge, background: '#ecfeff', color: '#0e7490', fontSize: 11 }}>{appRegisterBadgeLabel(r)}</span>}
                                            {dup && <span style={{ ...U.badge, background: '#ffedd5', color: '#9a3412', fontSize: 11 }}>동명이인 주의</span>}
                                        </span>
                                        <span style={{ fontSize: 12, color: '#64748b', whiteSpace: 'nowrap' }}>
                                            {/* 정본 v2 L-4: {생년월일} · ***-{끝4자리} */}
                                            {r.birthDate ? `${r.birthDate} · ` : (r.birthYear ? `${r.birthYear}년 · ` : '')}
                                            {r.phoneLast4 ? `***-${r.phoneLast4}` : '번호 없음'}
                                        </span>
                                    </button>
                                );
                            })}
                </div>
            </div>
        </div>
    );
}

export default ClinicPatientPicker;
